import { queryClient } from "app/global";
import { fetchInstructorProfile } from "services/instructors";
import { fetchStudentProfile } from "services/student";
import { Student } from "services/student/model";
import { Degree, User, UserProfile, UserTimezone } from "services/users/model";
import { defineApiQuery } from "utils/queries";
import { checkServerTimeDifference } from "utils/date";
import { api } from "../restUtil";
import { hasRole } from "./utils";

export const currentUserQuery = defineApiQuery<User | null>({
  key: "combinedCurrentUserData",
  queryFn: async () => {
    const userProfile = await fetchUserProfile();
    if (!userProfile) {
      // null means unauthorized, i.e. no session
      return null;
    }

    const userWithAddonts: User = userProfile;
    if (hasRole("student", userProfile)) {
      const studentProfile = await fetchStudentProfile();
      userWithAddonts.student = studentProfile;
    } else if (hasRole("instructor", userProfile)) {
      const instructorProfile = await fetchInstructorProfile();
      userWithAddonts.instructor = instructorProfile;
    }

    checkServerTimeDifference(userProfile);

    return userWithAddonts;
  },
  staleTime: Infinity,
});

export async function refreshCurrentUser() {
  queryClient.invalidateQueries(currentUserQuery());
  const response = await queryClient.fetchQuery(currentUserQuery());
  if (!response) {
    throw new Error("User session has expired");
  }
  return response;
}

export async function fetchUserProfile() {
  let unauthorized = false;
  try {
    const response = await api.get<UserProfile>("/user/profile", undefined, {
      onUnauthorized: () => {
        unauthorized = true;
      },
    });
    return response;
  } catch (error) {
    if (unauthorized) {
      // TODO
      // clearUserSession();
      return null;
    }
    throw error;
  }
}

export type UserProfilePatchPayload = {
  first_name?: string;
  middle_name?: string | null;
  last_name?: string;
  nickname?: string | null;
  gender?: "male" | "female";
  liked_on_facebook?: boolean;
  street_1?: string | null;
  street_2?: string | null;
  city?: string;
  state?: string | null;
  zip?: string | null;
  country?: string;
  phone_home?: string | null;
  phone_mobile?: string | null;
  phone_work?: string | null;
  alt_email?: string | null;
  skype?: string | null;
  native_language?: string | null;
  localization_lang?: string;
  bio?: string | null;
  hobbies?: string | null;
  timezone?: string;
  title?: string | null;
};

export function patchUser(payload: UserProfilePatchPayload) {
  return api.patch<UserProfilePatchPayload>("/user/profile", payload);
}

type UserPublicProfileResponse = {
  id: string;
  last_seen: string | null;
  level: User["level"];
  company: User["company"];
  avatar: string;
  degrees: Degree[];
  profile: {
    first_name: string;
    address: {
      city: string | null;
      country: string | null;
    };
    native_language: string | null;
    timezone: UserTimezone;
    bio: string | null;
    hobbies: string | null;
    title: string | null;
  };
};

type UserProfileByIdParams = {
  id: string;
};

export const userProfileByIdQuery = defineApiQuery<
  UserPublicProfileResponse,
  UserProfileByIdParams
>({
  key: "/user/:id/profile",
  queryFn: ({ params }) =>
    api.get<UserPublicProfileResponse>(`/user/${params.id}/profile`),
});

export function getUserProfileById(args: UserProfileByIdParams) {
  return api.get<UserPublicProfileResponse>(`/user/${args.id}/profile`);
}

type PatchMFAStatePayload = {
  require_otp?: boolean;
  code: string;
};

export function patchMFAState(args: PatchMFAStatePayload) {
  return api.patch<PatchMFAStatePayload, void>("/user/mfa", args);
}

type UploadUserAvatarPayload = {
  file: Blob | File;
  contentType?: string;
};

export function uploadUserAvatar(args: UploadUserAvatarPayload) {
  return api.post<Blob | File, void>("/user/profile/avatar", args.file, {
    // `file.type` can be "" for files the browser can't MIME-type, hence the fallback.
    headers: {
      "Content-Type": args.contentType || args.file.type || "application/octet-stream",
    },
  });
}

export const ownStudentQuery = defineApiQuery<Student>({
  key: "/student",
});

type UserLastSeenParams = {
  id: string;
};

type UserLastSeenResponse = {
  last_seen: string | null;
};

export function getUserLastSeen(args: UserLastSeenParams) {
  return api.get<UserLastSeenResponse>(`/user/${args.id}/profile/lastseen`);
}

type DegreeListResponse = {
  list: Degree[];
};

export function getUserEducationList() {
  return api.get<DegreeListResponse>("/user/degrees");
}

export function getUserEducationListById(userId: string) {
  return api.get<DegreeListResponse>(`/user/${userId}/degrees`);
}

export * from "./utils";
